import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ClientPortalDialog, magicLinkUrl } from "@/components/ClientPortalDialog";
import { toast } from "sonner";
import { Globe, Copy, Send } from "lucide-react";

const TOGGLES = [
  ["progress", "Progress & tasks"],
  ["photos", "Site photos"],
  ["diary", "Site diary updates"],
  ["invoices", "Invoices"],
  ["variations", "Variations"],
  ["documents", "Plans & documents"],
];

export function ClientPortalCard({ job }) {
  const [access, setAccess] = useState(null);
  const [settings, setSettings] = useState({ progress: true, photos: true, diary: true, invoices: true, variations: true, documents: true, ...(job.portal_settings || {}) });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAccess = () => {
    if (!job.client_id) return;
    api.get(`/clients/${job.client_id}/portal`).then((r) => setAccess(r.data)).catch(() => {});
  };
  const loadComments = () => {
    api.get(`/jobs/${job.id}/portal-comments`).then((r) => setComments(r.data)).catch(() => {});
  };
  useEffect(() => { loadAccess(); loadComments(); /* eslint-disable-next-line */ }, [job.id, job.client_id]);

  const toggle = async (key, val) => {
    const next = { ...settings, [key]: val };
    setSettings(next);
    try {
      await api.patch(`/jobs/${job.id}/portal-settings`, { [key]: val });
    } catch {
      setSettings(settings);
      toast.error("Could not update");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(magicLinkUrl(access.magic_token));
    toast.success("Portal link copied");
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/portal-comments`, { text: reply });
      setReply("");
      loadComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <section className="hard-card p-5" data-testid="client-portal-card">
      <div className="label-tag mb-3 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Client portal</div>

      {!job.client_id ? (
        <p className="text-sm text-zinc-500">Link a client to this job to share a live progress portal with them.</p>
      ) : (
        <>
          {access?.enabled ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 text-[11px] font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
              </span>
              <Button size="sm" variant="outline" onClick={copyLink} className="rounded-lg h-8 text-xs font-semibold" data-testid="portal-card-copy-link">
                <Copy className="w-3 h-3 mr-1.5" /> Copy portal link
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDialogOpen(true)} className="rounded-lg h-8 text-xs font-semibold text-zinc-500" data-testid="portal-card-manage">
                Manage access
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-zinc-500">Give {job.client_name || "this client"} a private link to follow progress, photos and invoices.</p>
              <Button size="sm" onClick={() => setDialogOpen(true)} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold shrink-0" data-testid="portal-card-enable">
                Set up portal
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
            {TOGGLES.map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-2 text-[13px] text-zinc-700 dark:text-zinc-300">
                <span>{label}</span>
                <Switch checked={settings[key] !== false} onCheckedChange={(v) => toggle(key, v)} data-testid={`portal-toggle-${key}`} />
              </label>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-zinc-200 dark:border-white/10">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Client messages</div>
            {comments.length === 0 ? (
              <div className="text-xs text-zinc-400">No messages from the client yet.</div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {comments.map((c) => (
                  <div key={c.id} className={`max-w-[90%] rounded-lg px-3 py-2 text-[13px] ${
                    c.author_type === "client" ? "bg-[#FF5F15]/10" : "ml-auto bg-zinc-100 dark:bg-[#151C27]"
                  }`} data-testid={`office-portal-comment-${c.id}`}>
                    <div className="text-[10px] font-bold text-zinc-500 mb-0.5">{c.author_type === "client" ? c.author_name : `${c.author_name} (you)`}</div>
                    {c.text}
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={sendReply} className="mt-3 flex items-end gap-2">
              <Textarea rows={1} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the client…" className="rounded-lg resize-none min-h-[38px]" data-testid="portal-card-reply-input" />
              <Button type="submit" disabled={busy || !reply.trim()} className="rounded-lg bg-zinc-900 dark:bg-white dark:text-black text-white h-9 w-9 p-0 shrink-0" data-testid="portal-card-reply-send" aria-label="Send reply">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </form>
          </div>
        </>
      )}

      {job.client_id && (
        <ClientPortalDialog
          clientId={job.client_id}
          clientName={job.client_name || "Client"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onChanged={loadAccess}
        />
      )}
    </section>
  );
}
